import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { CreateBlockDto, CreateEditBlockResultDto, FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  planGuid: string;
  guid: string;
  name: string;
  hintText: string;
  sortOrder: string;
  createdByUserId: string;
};

const useCreateBlockRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createBlockRequest(variables: Variables): Promise<CreateEditBlockResultDto> {
    const accessToken = await getAccessToken();
    const body: CreateBlockDto = {
      planGuid: variables.planGuid,
      guid: variables.guid,
      name: variables.name,
      hintText: variables.hintText,
      sortOrder: variables.sortOrder,
      createdByUserId: variables.createdByUserId,
    };

    const response = await fetch(`${configuration.apiRootUrl}/blocks`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { createBlockRequest };
};

const useCreateBlock = () => {
  const { createBlockRequest } = useCreateBlockRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: createBlockRequest,
    onSuccess: (_data, variables) => {
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getStructure(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useCreateBlock };
